import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Button, TextField, Autocomplete, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Checkbox,
  FormControlLabel, Chip, Stack, ToggleButton, ToggleButtonGroup, Alert,
  CircularProgress, Snackbar, InputAdornment, MenuItem, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import {
  collection, doc, getDoc, getDocs, query, where, writeBatch, serverTimestamp,
  increment, updateDoc, arrayUnion,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import PatientSearch from '../../components/PatientSearch';
import { generateBoletaPDF } from '../../services/boletaPDF';
import { getTurnoActivo, siguienteFolioBoleta } from '../../services/cajaService';
import { trackMetaEventServer } from '../../services/metaPixel';
import { ORDER_COLLECTION_BY_TYPE, TIPO_ITEM_BY_ORDER_TYPE } from '../../services/ventaService';
import {
  CLP, FINANCE_COLORS, MEDIOS_PAGO, netoDesdeBruto, ivaDesdeBruto, aplicarDescuentoPct,
  getArancelPrice, getArancelBranches, getConvenioDiscount, findConvenioByName,
} from '../../utils/finance';

const { PRIMARY, TEAL, SUCCESS, AMBER, DANGER } = FINANCE_COLORS;

// Límite de descuento según rol
const maxDescuento = (role) => (role === 'admin' ? 50 : 10);

export default function TicketVenta() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const { activeBranchId, activeBranch, filterByBranch } = useBranch();
  const role = userData?.role;

  const labOrderId = params.get('labOrderId');
  const presupuestoId = params.get('presupuestoId');
  const patientIdParam = params.get('patientId');

  const [turno, setTurno] = useState(null);
  const [turnoLoading, setTurnoLoading] = useState(true);
  const [aranceles, setAranceles] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [selPatient, setSelPatient] = useState(null);
  const [loadingCargos, setLoadingCargos] = useState(false);

  // Ítems del ticket: { key, tipo, descripcion, cantidad, precioUnitario, descuento(%),
  //   incluido, referenceId, referenceType, arancelId, unidadNegocio, tipoPrestacion, chargePatientId }
  const [items, setItems] = useState([]);

  // Ítem manual en construcción
  const [manualArancel, setManualArancel] = useState(null);
  const [manualCant, setManualCant] = useState(1);
  const [manualDesc, setManualDesc] = useState(0);

  // Documento y pago
  const [tipoDocumento, setTipoDocumento] = useState('boleta');
  const [rutEmpresa, setRutEmpresa] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [medioPrincipal, setMedioPrincipal] = useState('efectivo');
  const [voucherPrincipal, setVoucherPrincipal] = useState('');
  const [last4Principal, setLast4Principal] = useState('');
  const [cuotas, setCuotas] = useState(1);
  const [montoRecibido, setMontoRecibido] = useState('');
  const [montoAPagar, setMontoAPagar] = useState('');
  const [dividido, setDividido] = useState(false);
  const [medioSec, setMedioSec] = useState('debito');
  const [montoPrincipal, setMontoPrincipal] = useState('');
  const [voucherSec, setVoucherSec] = useState('');
  const [last4Sec, setLast4Sec] = useState('');
  const [boletaNumber, setBoletaNumber] = useState('');

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [successVenta, setSuccessVenta] = useState(null);
  const [presupuestoData, setPresupuestoData] = useState(null);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const selConvenio = useMemo(() => findConvenioByName(convenios, selPatient?.ownerConvenio), [convenios, selPatient]);

  // ── Cargar turno activo + aranceles ──────────────────────────────────────
  useEffect(() => {
    (async () => {
      setTurnoLoading(true);
      try {
        const currentBranch = activeBranchId || userData?.branchId || '';
        const t = await getTurnoActivo({ branchId: currentBranch });
        setTurno(t);
        const [aSnap, cSnap] = await Promise.all([
          getDocs(query(collection(db, 'aranceles'))),
          getDocs(collection(db, 'convenios')),
        ]);
        setAranceles(
          aSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => {
            if (a.activo === false) return false;
            const branches = getArancelBranches(a);
            return branches.length === 0 || branches.includes(currentBranch);
          })
        );
        setConvenios(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.isActive !== false));
      } catch (e) {
        notify('Error al cargar datos de caja: ' + e.message, 'error');
      } finally {
        setTurnoLoading(false);
      }
    })();
  }, [activeBranchId, userData?.branchId]);

  // Precargar número de boleta sugerido al abrir el turno
  useEffect(() => {
    if (turno && !boletaNumber) {
      siguienteFolioBoleta().then(setBoletaNumber).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turno]);

  // ── Cargar cargos pendientes al seleccionar paciente ──────────────────────
  const cargarCargosPendientes = useCallback(async (patient) => {
    if (!patient?.patientId) return;
    setLoadingCargos(true);
    try {
      const convenio = findConvenioByName(convenios, patient.ownerConvenio);
      const nuevos = [];
      // a) Órdenes de laboratorio no pagadas
      const loSnap = await getDocs(query(
        collection(db, 'labOrders'),
        where('patientId', '==', patient.patientId),
        where('paymentStatus', '==', 'no_pagado'),
      ));
      loSnap.forEach((d) => {
        const o = { id: d.id, ...d.data() };
        if (presupuestoId && o.presupuestoId) return; // evitar duplicado si se paga el presupuesto asociado
        const exams = o.examsRequested || o.exams || [];
        const bruto = exams.reduce((s, e) => s + (Number(e.precio || e.precioNeto || e.price) || 0), 0)
          || Number(o.total) || 0;
        nuevos.push({
          key: `lab-${o.id}`, tipo: 'laboratorio',
          descripcion: `Orden Lab ${o.orderNumber || o.id.slice(0, 6)} (${exams.length} exámenes)`,
          cantidad: 1, precioUnitario: bruto, descuento: getConvenioDiscount(convenio, 'Examen', null), incluido: true,
          referenceId: o.id, referenceType: 'labOrder',
          arancelId: null, unidadNegocio: 'Laboratorio', tipoPrestacion: 'Examen',
        });
      });
      // b) Cargos de patient_accounts pendientes
      const chSnap = await getDocs(query(
        collection(db, 'patient_accounts', patient.patientId, 'charges'),
        where('status', '==', 'pending'),
      ));
      chSnap.forEach((d) => {
        const c = { id: d.id, ...d.data() };
        const tipoPrestacion = c.type || 'Cargo';
        nuevos.push({
          key: `charge-${c.id}`, tipo: c.referenceType === 'surgery' ? 'cirugia' : (c.type === 'medication' ? 'farmacia' : 'hospitalizacion'),
          descripcion: c.description || 'Cargo',
          cantidad: c.quantity || 1,
          precioUnitario: Number(c.unitPrice) || Number(c.totalAmount) || 0,
          descuento: getConvenioDiscount(convenio, tipoPrestacion, null), incluido: true,
          referenceId: c.referenceId || null, referenceType: c.referenceType || 'charge',
          arancelId: null, unidadNegocio: 'Clínica', tipoPrestacion,
          chargePatientId: patient.patientId, chargeId: c.id,
        });
      });
      // Merge sin duplicar
      setItems((prev) => {
        const existing = new Set(prev.map((p) => p.key));
        return [...prev, ...nuevos.filter((n) => !existing.has(n.key))];
      });
      if (nuevos.length === 0) notify('Sin cargos pendientes para este paciente', 'info');
    } catch (e) {
      notify('Error al cargar cargos: ' + e.message, 'error');
    } finally {
      setLoadingCargos(false);
    }
  }, [presupuestoId, convenios]);

  const handleSelectPatient = (p, skipCargos = false) => {
    setSelPatient(p);
    if (!skipCargos) cargarCargosPendientes(p);
  };

  // ── Pre-carga desde labOrderId (resuelve su paciente) ─────────────────────
  useEffect(() => {
    (async () => {
      if (!labOrderId || selPatient) return;
      try {
        const loSnap = await getDoc(doc(db, 'labOrders', labOrderId));
        if (!loSnap.exists()) return;
        const o = loSnap.data();
        if (!o.patientId) return;
        const pSnap = await getDoc(doc(db, 'patients', o.patientId));
        const p = pSnap.exists() ? pSnap.data() : {};
        let owner = {};
        if (p.ownerId) {
          const oSnap = await getDoc(doc(db, 'owners', p.ownerId));
          if (oSnap.exists()) owner = oSnap.data();
        }
        handleSelectPatient({
          patientId: o.patientId, id: o.patientId, name: p.name || o.patientName, species: p.species,
          ownerId: p.ownerId || '', ownerName: owner.name || o.ownerName || '', ownerRut: owner.rut || '',
          ownerConvenio: owner.convenio || '',
        });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labOrderId]);

  // ── Pre-carga desde presupuestoId / patientId (query params) ──
  useEffect(() => {
    (async () => {
      if (patientIdParam && !selPatient) {
        try {
          const pSnap = await getDoc(doc(db, 'patients', patientIdParam));
          if (pSnap.exists()) {
            const p = pSnap.data();
            let owner = {};
            if (p.ownerId) {
              const oSnap = await getDoc(doc(db, 'owners', p.ownerId));
              if (oSnap.exists()) owner = oSnap.data();
            }
            const patient = {
              patientId: patientIdParam, id: patientIdParam, name: p.name, species: p.species,
              ownerId: p.ownerId || '', ownerName: owner.name || '', ownerRut: owner.rut || '',
              ownerCelular: owner.celular || '', ownerMail: owner.mail || owner.email || '',
              ownerConvenio: owner.convenio || '',
            };
            handleSelectPatient(patient);
          }
        } catch (_) { /* noop */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientIdParam]);

  // Pre-carga de presupuesto aprobado y paciente asociado
  useEffect(() => {
    (async () => {
      if (!presupuestoId) return;
      try {
        const pSnap = await getDoc(doc(db, 'presupuestos', presupuestoId));
        if (!pSnap.exists()) return;
        const pr = pSnap.data();
        setPresupuestoData({ id: pSnap.id, ...pr });

        // Cargar paciente del presupuesto
        if (pr.patientId) {
          const patSnap = await getDoc(doc(db, 'patients', pr.patientId));
          if (patSnap.exists()) {
            const pat = patSnap.data();
            let owner = {};
            if (pat.ownerId) {
              const oSnap = await getDoc(doc(db, 'owners', pat.ownerId));
              if (oSnap.exists()) owner = oSnap.data();
            }
            handleSelectPatient({
              patientId: pr.patientId,
              id: pr.patientId,
              name: pat.name,
              species: pat.species,
              ownerId: pat.ownerId || '',
              ownerName: owner.name || '',
              ownerRut: owner.rut || '',
              ownerCelular: owner.celular || '',
              ownerMail: owner.mail || owner.email || '',
              ownerConvenio: owner.convenio || '',
            }, true);
          }
        }

        const tipoItem = TIPO_ITEM_BY_ORDER_TYPE[pr.orderType] || 'otro';
        setItems((prev) => {
          const existing = new Set(prev.map((it) => it.key));
          const newItems = (pr.items || []).map((it, i) => {
            const sinDesc = (Number(it.precioUnitario) || 0) * (Number(it.cantidad) || 1);
            const conDesc = it.total != null ? it.total : (sinDesc - (Number(it.descuento) || 0));
            const descPct = sinDesc > 0 ? Math.round(((sinDesc - conDesc) / sinDesc) * 100) : 0;
            return {
              key: `pres-${i}`, tipo: tipoItem, descripcion: it.prestacion || it.descripcion,
              cantidad: it.cantidad || 1, precioUnitario: it.precioUnitario || 0,
              descuento: descPct, incluido: true,
              referenceId: presupuestoId, referenceType: 'presupuesto',
              arancelId: it.arancelId || null, unidadNegocio: it.unidadNegocio || '',
              tipoPrestacion: it.tipoPrestacion || '',
            };
          }).filter((it) => !existing.has(it.key));
          return [...prev, ...newItems];
        });
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presupuestoId]);

  // ── Agregar ítem manual del arancel ───────────────────────────────────────
  const addManual = () => {
    if (!manualArancel) { notify('Selecciona una prestación del arancel', 'warning'); return; }
    const convenioPct = getConvenioDiscount(selConvenio, manualArancel.tipoPrestacionName || manualArancel.tipoPrestacion || '', manualArancel.id);
    const manualPct = Number(manualDesc) || 0;
    const pct = manualPct > 0 ? manualPct : convenioPct;
    if (pct > maxDescuento(role)) {
      notify(`Descuento máximo permitido para tu rol: ${maxDescuento(role)}%`, 'warning');
      return;
    }
    setItems((prev) => [...prev, {
      key: `manual-${Date.now()}`, tipo: 'otro',
      descripcion: manualArancel.prestacion,
      cantidad: Math.max(1, Number(manualCant) || 1),
      precioUnitario: getArancelPrice(manualArancel, activeBranchId || userData?.branchId),
      descuento: pct, incluido: true,
      referenceId: null, referenceType: 'arancel',
      arancelId: manualArancel.id, unidadNegocio: manualArancel.unidadNegocioName || '',
      tipoPrestacion: manualArancel.tipoPrestacionName || '',
    }]);
    setManualArancel(null); setManualCant(1); setManualDesc(0);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const toggleItem = (key) => setItems((prev) => prev.map((i) => i.key === key ? { ...i, incluido: !i.incluido } : i));
  const updateItem = (key, field, value) => setItems((prev) => prev.map((i) => i.key === key ? { ...i, [field]: value } : i));

  // ── Cálculo de totales (precios incluyen IVA) ─────────────────────────────
  const totales = useMemo(() => {
    const incl = items.filter((i) => i.incluido);
    let bruto = 0, descuentoTotal = 0;
    incl.forEach((i) => {
      const lineaSinDesc = (Number(i.precioUnitario) || 0) * (Number(i.cantidad) || 1);
      const lineaConDesc = aplicarDescuentoPct(lineaSinDesc, i.descuento);
      bruto += lineaConDesc;
      descuentoTotal += (lineaSinDesc - lineaConDesc);
    });
    const neto = netoDesdeBruto(bruto);
    const iva = ivaDesdeBruto(bruto);
    return { bruto, neto, iva, descuentoTotal, total: Math.round(bruto), count: incl.length };
  }, [items]);

  // ── Saldo pendiente del presupuesto (acumulado de pagos anteriores) ────────
  const presupuestoSaldoPendiente = useMemo(() => {
    const total = totales.total;
    if (!presupuestoId || !total) return total;
    const pagado = Number(presupuestoData?.montoPagado) || 0;
    if (pagado > 0) return Math.max(0, total - pagado);
    const saldo = Number(presupuestoData?.saldoPendiente);
    return Number.isFinite(saldo) && saldo >= 0 ? saldo : total;
  }, [presupuestoId, totales.total, presupuestoData?.montoPagado, presupuestoData?.saldoPendiente]);

  // Sincronizar monto a pagar con el saldo pendiente del presupuesto
  useEffect(() => {
    if (!montoAPagar && totales.total > 0 && presupuestoSaldoPendiente > 0) setMontoAPagar(String(presupuestoSaldoPendiente));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totales.total, montoAPagar, presupuestoSaldoPendiente]);

  // ── Cálculo de pagos / vuelto ─────────────────────────────────────────────
  const pagoInfo = useMemo(() => {
    const aPagar = Math.min(presupuestoSaldoPendiente, Math.max(0, Math.round(Number(montoAPagar) || 0)));
    const basePago = (medio, monto, voucher, last4, cuotas) => ({
      medio, monto,
      boletaNumber: boletaNumber.trim() || null,
      voucherNumber: voucher.trim() || null,
      last4Digits: last4.trim() || null,
      cuotas,
    });

    if (!dividido) {
      const recibido = medioPrincipal === 'efectivo' ? (Number(montoRecibido) || 0) : aPagar;
      const vuelto = medioPrincipal === 'efectivo' ? Math.max(0, recibido - aPagar) : 0;
      const cuotasNum = medioPrincipal === 'credito' ? Number(cuotas) || 1 : null;
      return {
        pagos: [basePago(medioPrincipal, aPagar, voucherPrincipal, last4Principal, cuotasNum)],
        vuelto,
        faltante: medioPrincipal === 'efectivo' ? Math.max(0, aPagar - recibido) : 0,
        saldoPendiente: Math.max(0, presupuestoSaldoPendiente - aPagar),
      };
    }
    const m1 = Math.min(aPagar, Math.max(0, Math.round(Number(montoPrincipal) || 0)));
    const m2 = Math.max(0, aPagar - m1);
    return {
      pagos: [
        basePago(medioPrincipal, m1, voucherPrincipal, last4Principal, medioPrincipal === 'credito' ? Number(cuotas) || 1 : null),
        basePago(medioSec, m2, voucherSec, last4Sec, null),
      ],
      vuelto: 0, faltante: 0,
      saldoPendiente: Math.max(0, presupuestoSaldoPendiente - aPagar),
    };
  }, [dividido, medioPrincipal, medioSec, montoPrincipal, montoAPagar, montoRecibido, voucherPrincipal, voucherSec, last4Principal, last4Sec, boletaNumber, cuotas, presupuestoSaldoPendiente]);

  const presupuestoPagado = presupuestoData?.estado === 'pagado' || presupuestoSaldoPendiente <= 0;
  const puedeCobrar = !presupuestoPagado && turno && totales.count > 0 && totales.total > 0 && !pagoInfo.faltante && pagoInfo.pagos.reduce((s, p) => s + p.monto, 0) > 0 && !saving;

  // ── Confirmar cobro (writeBatch atómico) ──────────────────────────────────
  const handleCobrar = async () => {
    if (presupuestoPagado) { notify('Este presupuesto ya fue pagado. No se puede pagar de nuevo.', 'error'); return; }
    if (!turno) { notify('No hay turno de caja abierto', 'error'); return; }
    if (totales.count === 0) { notify('Agrega al menos un ítem al ticket', 'warning'); return; }
    setSaving(true);
    try {
      const folio = boletaNumber.trim() || await siguienteFolioBoleta();
      const montoEstePago = pagoInfo.pagos.reduce((s, p) => s + p.monto, 0);
      const montoYaPagado = Number(presupuestoData?.montoPagado) || 0;
      const nuevoMontoPagado = montoYaPagado + montoEstePago;
      const nuevoSaldoPendiente = Math.max(0, totales.total - nuevoMontoPagado);
      const esPagoTotal = nuevoSaldoPendiente === 0;
      const batch = writeBatch(db);
      const ventaRef = doc(collection(db, 'Sales'));
      const incl = items.filter((i) => i.incluido).map((i) => {
        const lineaSinDesc = (Number(i.precioUnitario) || 0) * (Number(i.cantidad) || 1);
        const lineaTotal = aplicarDescuentoPct(lineaSinDesc, i.descuento);
        return {
          tipo: i.tipo, descripcion: i.descripcion, cantidad: Number(i.cantidad) || 1,
          precioUnitario: Number(i.precioUnitario) || 0, descuento: lineaSinDesc - lineaTotal,
          subtotal: netoDesdeBruto(lineaTotal), iva: ivaDesdeBruto(lineaTotal), total: lineaTotal,
          referenceId: i.referenceId || null, referenceType: i.referenceType || null,
          arancelId: i.arancelId || null, unidadNegocio: i.unidadNegocio || '', tipoPrestacion: i.tipoPrestacion || '',
        };
      });

      const ventaData = {
        // referencias de trazabilidad
        presupuestoId: presupuestoId || null,
        presupuestoNumber: presupuestoData?.numero || '',
        orderId: presupuestoData?.orderId || null,
        orderType: presupuestoData?.orderType || '',
        orderNumber: presupuestoData?.orderNumber || '',
        // compat con Sales existente
        patientId: selPatient?.patientId || '', patientName: selPatient?.name || '',
        total: montoEstePago, subtotal: netoDesdeBruto(montoEstePago), iva: ivaDesdeBruto(montoEstePago),
        paymentMethod: pagoInfo.pagos[0]?.medio || 'efectivo',
        boletaNumber: folio,
        paidAt: serverTimestamp(), paidBy: currentUser?.uid || '', paidByName: userData?.name || currentUser?.email || '',
        createdAt: serverTimestamp(),
        // campos nuevos
        turnoId: turno.id, cajaId: turno.cajaId, cajaNombre: turno.cajaNombre,
        branchId: turno.branchId || userData?.branchId || '',
        ownerId: selPatient?.ownerId || '', ownerName: selPatient?.ownerName || '', ownerRut: selPatient?.ownerRut || '',
        convenioId: selConvenio?.id || '', convenioName: selConvenio?.name || selPatient?.ownerConvenio || '',
        tipoDocumento, folioDocumento: folio, folioSII: null,
        rutEmpresa: tipoDocumento === 'factura' ? rutEmpresa : '', razonSocial: tipoDocumento === 'factura' ? razonSocial : '',
        items: incl,
        subtotalNeto: netoDesdeBruto(montoEstePago), montoIVA: ivaDesdeBruto(montoEstePago), montoExento: 0,
        descuentoTotal: totales.descuentoTotal, totalFinal: montoEstePago,
        saldoPendiente: nuevoSaldoPendiente,
        pagos: pagoInfo.pagos, vuelto: pagoInfo.vuelto,
        estado: 'emitida', anulacionMotivo: null, anulacionAt: null, anulacionBy: null,
      };
      batch.set(ventaRef, ventaData);

      // Actualizar labOrders incluidos
      items.filter((i) => i.incluido && i.referenceType === 'labOrder').forEach((i) => {
        batch.update(doc(db, 'labOrders', i.referenceId), {
          paymentStatus: 'pagado', paymentId: ventaRef.id,
        });
      });
      // Actualizar charges incluidos
      items.filter((i) => i.incluido && i.chargeId && i.chargePatientId).forEach((i) => {
        batch.update(doc(db, 'patient_accounts', i.chargePatientId, 'charges', i.chargeId), {
          status: 'billed', salesId: ventaRef.id,
        });
      });
      // Actualizar presupuesto y orden asociada
      if (presupuestoId) {
        batch.update(doc(db, 'presupuestos', presupuestoId), {
          estado: esPagoTotal ? 'pagado' : 'abonado',
          ventaId: ventaRef.id,
          montoPagado: nuevoMontoPagado,
          saldoPendiente: nuevoSaldoPendiente,
          updatedAt: serverTimestamp(),
          ...(presupuestoData?.branchId ? {} : { branchId: activeBranchId || userData?.branchId || '' }),
        });
        const orderCollection = ORDER_COLLECTION_BY_TYPE[presupuestoData?.orderType];
        if (esPagoTotal && presupuestoData?.orderId && orderCollection) {
          const orderDocRef = doc(db, orderCollection, presupuestoData.orderId);
          const orderSnap = await getDoc(orderDocRef);
          if (orderSnap.exists()) {
            const orderUpdate = {
              paymentStatus: 'pagado',
              paymentId: ventaRef.id,
              presupuestoId,
              paidAt: serverTimestamp(),
            };
            if (presupuestoData.orderType === 'labOrder') orderUpdate.status = 'pending_upload';
            batch.update(orderDocRef, orderUpdate);
          }
        }
      }

      // Sumar al turno
      const totEfectivo = pagoInfo.pagos.filter((p) => p.medio === 'efectivo').reduce((s, p) => s + p.monto, 0);
      const totDebito = pagoInfo.pagos.filter((p) => p.medio === 'debito').reduce((s, p) => s + p.monto, 0);
      const totCredito = pagoInfo.pagos.filter((p) => p.medio === 'credito').reduce((s, p) => s + p.monto, 0);
      const totTransf = pagoInfo.pagos.filter((p) => p.medio === 'transferencia').reduce((s, p) => s + p.monto, 0);
      const totOtros = pagoInfo.pagos.filter((p) => ['webpay', 'convenio', 'otro'].includes(p.medio)).reduce((s, p) => s + p.monto, 0);
      batch.update(doc(db, 'turnosCaja', turno.id), {
        totalEfectivo: increment(totEfectivo), totalDebito: increment(totDebito),
        totalCredito: increment(totCredito), totalTransferencia: increment(totTransf),
        totalOtros: increment(totOtros), totalBruto: increment(montoEstePago),
        totalNeto: increment(netoDesdeBruto(montoEstePago)), totalIVA: increment(ivaDesdeBruto(montoEstePago)),
        totalDescuentos: increment(totales.descuentoTotal), conteoVentas: increment(1),
      });

      await batch.commit();

      // Actualizar el contact-request vinculado, si existe
      if (presupuestoData?.contactRequestId) {
        try {
          const crRef = doc(db, 'contact-requests', presupuestoData.contactRequestId);
          const crSnap = await getDoc(crRef);
          if (crSnap.exists()) {
            const cr = crSnap.data();
            const currentStatus = cr.status || 'Nuevo';
            if (!['Atendido', 'Cerrado'].includes(currentStatus)) {
              const newStatus = esPagoTotal ? 'Atendido' : 'Abonado';
              await updateDoc(crRef, {
                status: newStatus,
                ventaId: ventaRef.id,
                presupuestoId: presupuestoId || null,
                logs: arrayUnion({
                  userName: userData?.name || userData?.email || 'Usuario',
                  userEmail: currentUser?.email || '',
                  timestamp: new Date(),
                  text: `Venta registrada. Boleta ${folio}. Presupuesto N° ${presupuestoData?.numero || ''} ${esPagoTotal ? 'pagado' : 'abonado'}.`,
                }),
              });
            }
          }
        } catch (crmErr) {
          console.error('Error actualizando contact-request desde TicketVenta:', crmErr);
        }
      }

      const ventaFull = { ...ventaData, id: ventaRef.id, createdAt: new Date() };
      setSuccessVenta(ventaFull);
      notify(`Venta registrada correctamente${esPagoTotal ? '' : ' (abono)'}`);

      // Meta Pixel: Purchase con el monto neto, sin exponer IP del usuario de la plataforma
      trackMetaEventServer({
        eventName: 'Purchase',
        value: ventaData.subtotalNeto,
        currency: 'CLP',
        owner: { name: ventaData.ownerName, rut: ventaData.ownerRut, ownerId: ventaData.ownerId },
        itemName: presupuestoData?.numero ? `Pago presupuesto ${presupuestoData.numero}` : 'Pago Caja',
        contentIds: [presupuestoData?.numero || folio || ventaRef.id],
        eventId: `purchase_${ventaRef.id}`,
      }).catch(() => {});
    } catch (e) {
      notify('Error al registrar la venta: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const imprimirBoleta = async () => {
    if (!successVenta) return;
    await generateBoletaPDF(
      successVenta,
      { name: successVenta.ownerName, rut: successVenta.ownerRut },
      { name: successVenta.patientName, species: selPatient?.species },
      { branch: activeBranch },
    );
  };

  const nuevaVenta = () => {
    setSuccessVenta(null); setSelPatient(null); setItems([]);
    setMontoAPagar(''); setMontoRecibido(''); setDividido(false); setMontoPrincipal('');
    setVoucherPrincipal(''); setVoucherSec(''); setLast4Principal(''); setLast4Sec('');
    setBoletaNumber(''); setCuotas(1); setTipoDocumento('boleta');
    navigate('/caja/ticket', { replace: true });
  };

  // ── Render: sin turno ──────────────────────────────────────────────────────
  if (turnoLoading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (!turno) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No hay un turno de caja abierto. Debes abrir la caja antes de registrar ventas.
        </Alert>
        <Button variant="contained" startIcon={<PointOfSaleIcon />} onClick={() => navigate('/caja')}>
          Ir a Caja
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/caja')}><ArrowBackIcon /></IconButton>
        <PointOfSaleIcon sx={{ color: PRIMARY }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color={PRIMARY}>Nueva Venta</Typography>
          <Typography variant="caption" color="text.secondary">
            {turno.cajaNombre} · Turno {turno.fechaTurno}
          </Typography>
        </Box>
        <Chip label={`Cajero: ${userData?.name || currentUser?.email || ''}`} size="small" />
      </Stack>

      {presupuestoPagado && (
        <Alert severity="error" sx={{ mb: 2 }}>
          El presupuesto {presupuestoData?.numero} ya fue pagado. No se puede cobrar de nuevo.
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Columna izquierda: paciente + ítems */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Paciente */}
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} sx={{ mb: 1 }}>
              1. Propietario / Paciente
            </Typography>
            {selPatient ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700}>{selPatient.name} <Typography component="span" variant="caption" color="text.secondary">({selPatient.species})</Typography></Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selPatient.ownerName}{selPatient.ownerRut ? ` · ${selPatient.ownerRut}` : ''}{selPatient.ownerConvenio ? ` · ${selPatient.ownerConvenio}` : ''}
                  </Typography>
                  {selConvenio && (
                    <Chip label={`Convenio: ${selConvenio.name}`} size="small" color="primary" sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
                  )}
                </Box>
                <Button size="small" onClick={() => { setSelPatient(null); setItems([]); }}>Cambiar</Button>
              </Stack>
            ) : (
              <PatientSearch onPatientSelect={handleSelectPatient} selectedPatient={selPatient} />
            )}
          </Paper>

          {/* Agregar ítem manual */}
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} sx={{ mb: 1 }}>
              2. Agregar prestación (arancel)
            </Typography>
            <Grid container spacing={1.5} alignItems="center">
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small" fullWidth options={aranceles}
                  value={manualArancel}
                  onChange={(_, v) => setManualArancel(v)}
                  getOptionLabel={(o) => {
                    const price = getArancelPrice(o, activeBranchId || userData?.branchId);
                    return o.prestacion ? `${o.prestacion} — ${CLP(price)}` : '';
                  }}
                  renderInput={(p) => <TextField {...p} label="Buscar prestación" InputLabelProps={{ shrink: true }} />}
                  componentsProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { display: 'block' } } } }}
                  renderOption={(props, o) => {
                    const price = getArancelPrice(o, activeBranchId || userData?.branchId);
                    return (
                      <li {...props} key={o.id}>
                        <Box>
                          <Typography variant="body2">{o.prestacion}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {o.tipoPrestacionName} · {o.unidadNegocioName} · {CLP(price)}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <TextField size="small" fullWidth type="number" label="Cant." value={manualCant}
                  onChange={(e) => setManualCant(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} />
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <TextField size="small" fullWidth type="number" label="Desc. %" value={manualDesc}
                  onChange={(e) => setManualDesc(e.target.value)} InputLabelProps={{ shrink: true }}
                  helperText={`Máx ${maxDescuento(role)}%`} inputProps={{ min: 0, max: 100 }} />
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={addManual} sx={{ bgcolor: TEAL }}>
                  Agregar
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabla de ítems */}
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} sx={{ mb: 1 }}>
              3. Detalle del ticket
            </Typography>
            {loadingCargos ? (
              <Stack spacing={1}>{[1, 2, 3].map((i) => <Skeleton key={i} height={32} />)}</Stack>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                Selecciona un paciente para cargar cargos pendientes o agrega prestaciones manualmente.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem' } }}>
                    <TableCell padding="checkbox" />
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center">Cant.</TableCell>
                    <TableCell align="right">P. Unit.</TableCell>
                    <TableCell align="center">Desc.%</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((i) => {
                    const lineaSinDesc = (Number(i.precioUnitario) || 0) * (Number(i.cantidad) || 1);
                    const lineaTotal = aplicarDescuentoPct(lineaSinDesc, i.descuento);
                    return (
                      <TableRow key={i.key} sx={{ opacity: i.incluido ? 1 : 0.4 }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={i.incluido} onChange={() => toggleItem(i.key)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{i.descripcion}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i.tipoPrestacion}{i.unidadNegocio ? ` · ${i.unidadNegocio}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: 70 }}>
                          <TextField size="small" type="number" value={i.cantidad}
                            onChange={(e) => updateItem(i.key, 'cantidad', Math.max(1, Number(e.target.value) || 1))}
                            inputProps={{ min: 1, style: { textAlign: 'center', width: 40 } }} variant="standard" />
                        </TableCell>
                        <TableCell align="right">{CLP(i.precioUnitario)}</TableCell>
                        <TableCell align="center" sx={{ width: 70 }}>
                          <TextField size="small" type="number" value={i.descuento}
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                              if (v > maxDescuento(role)) { notify(`Descuento máx ${maxDescuento(role)}%`, 'warning'); return; }
                              updateItem(i.key, 'descuento', v);
                            }}
                            inputProps={{ min: 0, max: 100, style: { textAlign: 'center', width: 40 } }} variant="standard" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(lineaTotal)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => removeItem(i.key)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>

        {/* Columna derecha: totales + pago */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, position: 'sticky', top: 80 }}>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} sx={{ mb: 1 }}>Resumen</Typography>

            {/* Tipo documento */}
            <TextField select size="small" fullWidth label="Documento" value={tipoDocumento}
              onChange={(e) => setTipoDocumento(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1 }}>
              <MenuItem value="boleta">Boleta</MenuItem>
              <MenuItem value="factura">Factura</MenuItem>
              <MenuItem value="exento">Sin documento</MenuItem>
            </TextField>
            {tipoDocumento === 'factura' && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" fullWidth label="RUT empresa" value={rutEmpresa}
                  onChange={(e) => setRutEmpresa(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" fullWidth label="Razón social" value={razonSocial}
                  onChange={(e) => setRazonSocial(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Stack>
            )}

            <Divider sx={{ my: 1 }} />
            {selConvenio && (
              <Chip label={`Convenio: ${selConvenio.name}`} size="small" color="primary" sx={{ mb: 1 }} />
            )}
            <Row label="Neto" value={CLP(totales.neto)} />
            <Row label="IVA (19%)" value={CLP(totales.iva)} />
            {totales.descuentoTotal > 0 && <Row label={`Descuentos ${selConvenio ? `(${selConvenio.name})` : ''}`} value={`-${CLP(totales.descuentoTotal)}`} color={DANGER} />}
            <Divider sx={{ my: 1 }} />
            <Row label="TOTAL" value={CLP(totales.total)} bold />
            {presupuestoId && Number(presupuestoData?.montoPagado) > 0 && (
              <Row label="Monto ya pagado" value={CLP(presupuestoData.montoPagado)} color={TEAL} />
            )}
            {presupuestoId && (
              <Row label="Saldo por pagar" value={CLP(presupuestoSaldoPendiente)} color={AMBER} bold />
            )}

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" fontWeight={700} color="text.secondary">PAGO</Typography>
            <TextField size="small" fullWidth type="number" label="Monto a pagar" value={montoAPagar}
              onChange={(e) => setMontoAPagar(e.target.value)} InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ my: 1 }} />
            {pagoInfo.saldoPendiente > 0 && (
              <Alert severity="info" sx={{ my: 1, py: 0 }}>Restante luego de este pago: {CLP(pagoInfo.saldoPendiente)}</Alert>
            )}

            <TextField size="small" fullWidth label="N° boleta SII" value={boletaNumber}
              onChange={(e) => setBoletaNumber(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1 }} />

            <ToggleButtonGroup exclusive size="small" value={medioPrincipal}
              onChange={(_, v) => v && setMedioPrincipal(v)} sx={{ display: 'flex', flexWrap: 'wrap', my: 1 }}>
              {MEDIOS_PAGO.slice(0, 5).map((m) => (
                <ToggleButton key={m.id} value={m.id} sx={{ flex: '1 0 30%', fontSize: '0.7rem', py: 0.5 }}>
                  {m.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {medioPrincipal === 'efectivo' && !dividido && (
              <>
                <TextField size="small" fullWidth type="number" label="Monto recibido" value={montoRecibido}
                  onChange={(e) => setMontoRecibido(e.target.value)} InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ mb: 1 }} />
                {Number(montoRecibido) > 0 && pagoInfo.vuelto > 0 && (
                  <Row label="Vuelto" value={CLP(pagoInfo.vuelto)} color={SUCCESS} bold />
                )}
              </>
            )}

            {['debito', 'credito', 'transferencia', 'webpay'].includes(medioPrincipal) && (
              <TextField size="small" fullWidth label="N° voucher / referencia"
                value={voucherPrincipal} onChange={(e) => setVoucherPrincipal(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1 }} />
            )}
            {['debito', 'credito', 'webpay'].includes(medioPrincipal) && (
              <TextField size="small" fullWidth label="Últimos 4 dígitos" value={last4Principal}
                onChange={(e) => setLast4Principal(e.target.value.replace(/\D/g, '').slice(0, 4))} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 4 }} sx={{ mb: 1 }} />
            )}
            {medioPrincipal === 'credito' && (
              <TextField size="small" fullWidth type="number" label="Cuotas" value={cuotas}
                onChange={(e) => setCuotas(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} sx={{ mb: 1 }} />
            )}

            <FormControlLabel
              control={<Checkbox size="small" checked={dividido} onChange={(e) => setDividido(e.target.checked)} />}
              label={<Typography variant="caption">Pago dividido</Typography>}
            />
            {dividido && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" fullWidth type="number" label={`Monto con ${MEDIOS_PAGO.find(m=>m.id===medioPrincipal)?.label}`}
                  value={montoPrincipal} onChange={(e) => setMontoPrincipal(e.target.value)} InputLabelProps={{ shrink: true }} />
                {['debito', 'credito', 'transferencia', 'webpay'].includes(medioPrincipal) && (
                  <TextField size="small" fullWidth label="N° voucher / referencia"
                    value={voucherPrincipal} onChange={(e) => setVoucherPrincipal(e.target.value)} InputLabelProps={{ shrink: true }} />
                )}
                {['debito', 'credito', 'webpay'].includes(medioPrincipal) && (
                  <TextField size="small" fullWidth label="Últimos 4 dígitos"
                    value={last4Principal} onChange={(e) => setLast4Principal(e.target.value.replace(/\D/g, '').slice(0, 4))} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 4 }} />
                )}
                {medioPrincipal === 'credito' && (
                  <TextField size="small" fullWidth type="number" label="Cuotas" value={cuotas}
                    onChange={(e) => setCuotas(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} />
                )}

                <TextField select size="small" fullWidth label="Segundo medio" value={medioSec}
                  onChange={(e) => setMedioSec(e.target.value)} InputLabelProps={{ shrink: true }}>
                  {MEDIOS_PAGO.filter((m) => m.id !== medioPrincipal).map((m) => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}
                </TextField>
                <Row label={`Resto (${MEDIOS_PAGO.find(m=>m.id===medioSec)?.label})`} value={CLP(pagoInfo.pagos[1]?.monto || 0)} />
                {['debito', 'credito', 'transferencia', 'webpay'].includes(medioSec) && (
                  <TextField size="small" fullWidth label="N° voucher / referencia"
                    value={voucherSec} onChange={(e) => setVoucherSec(e.target.value)} InputLabelProps={{ shrink: true }} />
                )}
                {['debito', 'credito', 'webpay'].includes(medioSec) && (
                  <TextField size="small" fullWidth label="Últimos 4 dígitos"
                    value={last4Sec} onChange={(e) => setLast4Sec(e.target.value.replace(/\D/g, '').slice(0, 4))} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 4 }} />
                )}
              </Stack>
            )}

            {pagoInfo.faltante > 0 && (
              <Alert severity="warning" sx={{ my: 1, py: 0 }}>Faltan {CLP(pagoInfo.faltante)}</Alert>
            )}

            <Button fullWidth variant="contained" size="large" disabled={!puedeCobrar}
              onClick={handleCobrar} startIcon={saving ? <CircularProgress size={16} /> : <PointOfSaleIcon />}
              sx={{ mt: 1, bgcolor: SUCCESS, fontWeight: 700, '&:hover': { bgcolor: '#0e9e6e' } }}>
              {saving ? 'Procesando…' : `Cobrar ${CLP(pagoInfo.pagos.reduce((s, p) => s + p.monto, 0))}`}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Diálogo de éxito */}
      <Dialog open={!!successVenta} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <CheckCircleIcon sx={{ color: SUCCESS, fontSize: 48 }} />
          <Typography variant="h6" fontWeight={800}>Venta registrada</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Boleta N° {successVenta?.folioDocumento}</Typography>
          <Typography variant="h4" fontWeight={800} color={PRIMARY} sx={{ my: 1 }}>{CLP(successVenta?.totalFinal)}</Typography>
          {successVenta?.vuelto > 0 && (
            <Chip color="success" label={`Vuelto: ${CLP(successVenta.vuelto)}`} sx={{ mb: 1 }} />
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button fullWidth variant="contained" startIcon={<ReceiptIcon />} onClick={imprimirBoleta}>Imprimir Boleta</Button>
          <Button fullWidth variant="outlined" onClick={nuevaVenta}>Nueva Venta</Button>
          <Button fullWidth onClick={() => navigate('/caja')}>Volver a Caja</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function Row({ label, value, bold, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.3 }}>
      <Typography variant={bold ? 'body1' : 'body2'} fontWeight={bold ? 800 : 400} color={bold ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
      <Typography variant={bold ? 'h6' : 'body2'} fontWeight={bold ? 800 : 600} sx={{ color: color || (bold ? PRIMARY : 'text.primary') }}>
        {value}
      </Typography>
    </Box>
  );
}
